import { OnDestroy, OnInit, ElementRef, Directive } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap, filter } from 'rxjs/operators';
import { PaginatedScrollService, PaginatedScrollController } from '@shared/services/paginated-scroll.service';
import { Learnspace, Hub, ListQueryOptions } from '@shared/models';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { LearnspaceEditDialogComponent } from '@app/main/learnspaces/learnspace-edit-dialog/learnspace-edit-dialog.component';
import { LearnspaceWelcomeDialogComponent } from '@app/main/learnspaces/learnspace-welcome-dialog/learnspace-welcome-dialog.component';
import { CurrentHubService } from '@shared/services/currentHub.service';
import { courseLabel, spaceLabel, StringCase } from '@shared/utils/wording.utils';

@Directive()
export abstract class LearnspaceListBaseComponent implements OnInit, OnDestroy {
  hub: Hub;
  nbMySpaces: number;
  nbAll: number;
  lsList: Learnspace[];
  joinedList: Learnspace[];
  refresh = new BehaviorSubject<string>(null);
  refreshObs = this.refresh.asObservable();
  url: string;
  isAdminOfHub = false;
  listingMode: string;
  protected _defaultListingMode: string = 'joined';
  // TODO: Sort direction (ASC, DESC) could be handled more carefully
  public sortOptions: { value: string; name: string; }[];
  public selectedSort = 'status,-updatedAt';

  protected _paginationController: PaginatedScrollController<Learnspace>;

  constructor(
    protected _courseApiService: CoursesAPIService,
    protected _dialog: MatDialog,
    protected _router: Router,
    protected _elRef: ElementRef,
    protected _currentHub: CurrentHubService,
    protected _paginatedScrollService: PaginatedScrollService
  ) { }

  ngOnInit(): void {
    this._currentHub.hubObservable.subscribe(hub => {
      if (hub) {
        this.hub = hub;
        this.countSpaces();
        this.setListMode(this._defaultListingMode);
      }

      this.sortOptions = [
        {
          value: 'title',
          name: `${spaceLabel(hub?.mode, false, StringCase.titlecase)} Title`
        },
        {
          value: 'status,-updatedAt',
          name: 'Last Modified'
        },
        {
          value: 'status,-experienceCount',
          name: `Number of ${courseLabel(hub?.mode, true, StringCase.titlecase)}`
        },
        {
          value: 'status,-participantCount',
          name: 'Number of Participants'
        }
      ];
    })
  }

  ngOnDestroy(): void {
    this._paginationController?.destroy();
  }

  setListMode(change: string): void {
    this.listingMode = change;
    this.reloadList();
  }

  reloadList(): void {
    this._paginationController?.destroy();
    this._paginationController = this._paginatedScrollService.createController(
      options => this._getRequest(options).pipe(
        map(({ total, learnspaces }) => ({ total, items: learnspaces }))
      ),
      items => this.lsList = items,
      { sort: this.selectedSort }
    );
    this._paginationController.reload().subscribe({ next: () => this._paginationController.attachScroll(this._elRef.nativeElement) });
  }

  sortList(): void {
    this._paginationController?.reload({ sort: this.selectedSort }).subscribe();
  }

  countSpaces(event?: any): void {
    if (event && event.shouldReloadList) {
      this.reloadList();
    }
    this._courseApiService.countLearnspacesInHub(this.hub.id)
      .subscribe(result => {
        this.nbMySpaces = result.joined;
        this.nbAll = result.all;
        this._currentHub.updateCanCreateSpace(result.all);
      });
  }

  canCreateSpace(): boolean {
    return this._currentHub.canCreateSpace;
  }

  hasRoleToCreateSpace(): boolean {
    return this._currentHub.canManageCurrentHub();
  }

  private _getRequest(options: ListQueryOptions) {
    if (this.listingMode === 'joined') {
      return this._courseApiService.getLearnspacesJoinedByHub(this.hub.id, options);
    }
    return this._courseApiService.getLearnspacesByHub(this.hub.id, options);
  }

  addLearnspace(): void {
    this._dialog
      .open(
        LearnspaceEditDialogComponent,
        {
          width: '50vw',
          data: { hubId: this.hub.id, mode: this.hub.mode }
        }
      )
      .afterClosed()
      .pipe(
        filter(a => a),
        map(ls => this._router.navigate([this.hub.urlTitle, ls.id]))
      ).subscribe();
  }

  displayWelcomeMessage(): void {
    this._dialog
      .open(LearnspaceWelcomeDialogComponent, { width: '45vw', height: '70vh' });
  }
}
